import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AuthService } from '@spartacus/core';
import { LaunchDialogService } from '@spartacus/storefront';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { CmsLinkComponentsService } from '../../../../core/cms';
import { LAUNCH_CALLER } from '../../../../core/modal';
import { Article, Certification, CMSLinkComponents, PdpTabTypes, Product, ProductCertification } from '../../../../core/model';
import { ProductCertificateGroupCustomizationEnum } from '../../../../core/occ/occ-models/occ-generated.models';
import { PrincipalConfigurationService } from '../../../../core/user';
import { ProductDetailsService } from '../../base/services';

@Component({
  selector: 'py-pdp-overview',
  templateUrl: './pdp-overview.component.html',
  styleUrls: ['./pdp-overview.component.scss'],
  providers: [ProductDetailsService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PdpOverviewComponent implements OnInit {
  private article$ = new BehaviorSubject<Article>(undefined);
  private product$ = new BehaviorSubject<Product>(undefined);

  @Input() set product(product: Product) {
    this.productDetailsService.setProduct(product);
    this.product$.next(product);
  }
  get product(): Product {
    return this.product$.value;
  }
  @Input() set article(article: Article) {
    this.article$.next(article);
  }
  get article(): Article {
    return this.article$.value;
  }
  @Input() showReadMore: boolean;
  @Input() outOfStock: boolean;
  @Input() replacementArticle: Article;
  @Input() pdpTypeSkuPicker = true;

  tabTypeArticleList = PdpTabTypes.ArticleList;

  @Output() readMore = new EventEmitter<PdpTabTypes | undefined>();

  isUserLoggedIn$: Observable<boolean>;
  enableContractedArticleIcon$: Observable<boolean>;
  enableSecondaryArticleRowVariant$: Observable<boolean>;
  cmsLinkComponents$: Observable<CMSLinkComponents[]>;
  certificates$ = combineLatest([this.article$, this.product$]).pipe(
    switchMap(
      ([article]): Observable<ProductCertification> =>
        !!article ? of(article.certLabels) : this.productDetailsService.getCertLabels()
    ),
    map((certLabels) =>
      certLabels?.groups?.reduce<Certification[]>(
        (certificates, group) => [
          ...certificates,
          ...(group?.customization === ProductCertificateGroupCustomizationEnum.DEFAULT ? group.certificates : []),
        ],
        []
      )
    ),
    map((certificates) => certificates?.filter((certificate) => !!certificate?.icon) ?? [])
  );
  hasCertificates$ = this.certificates$.pipe(map((certificates) => certificates?.length > 0));

  constructor(
    private authService: AuthService,
    private principalConfigurationService: PrincipalConfigurationService,
    private cmsLinkComponentsService: CmsLinkComponentsService,
    private launchDialogService: LaunchDialogService,
    private productDetailsService: ProductDetailsService
  ) {}

  ngOnInit(): void {
    this.cmsLinkComponents$ = this.cmsLinkComponentsService.getLinksByProduct(this.product.code);

    this.isUserLoggedIn$ = this.authService
      .isUserLoggedIn()
      .pipe(startWith(false), shareReplay({ bufferSize: 1, refCount: true }));

    this.enableContractedArticleIcon$ = this.principalConfigurationService.isEnabled('enableContractedArticleIcon');

    this.enableSecondaryArticleRowVariant$ = this.principalConfigurationService.isEnabled('enableSecondaryArticleRowVariant');
  }

  clickReadMore(tab?: PdpTabTypes) {
    this.readMore.emit(tab);
  }

  openSampleOrderModal(): void {
    this.launchDialogService.openDialogAndSubscribe(LAUNCH_CALLER.SAMPLE_ORDER, undefined, this.product);
  }

  openInquiryModal(): void {
    this.launchDialogService.openDialogAndSubscribe(LAUNCH_CALLER.PDP_INQUIRY, undefined, this.product);
  }
}
